/**
 * Dynamic Breadcrumb Tracker
 * Tracks user navigation path and renders dynamic breadcrumbs
 */

class BreadcrumbTracker {
  constructor() {
    this.TRAIL_KEY = "breadcrumb_trail";
    this.PENDING_CLICK_KEY = "breadcrumb_pending_click";
    this.MAX_TRAIL_LENGTH = 5;

    this.init();
  }

  init() {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => this.onPageLoad());
    } else {
      this.onPageLoad();
    }
  }

  onPageLoad() {
    // Handle the trail based on current page
    this.handleTrail();

    // Render breadcrumbs
    this.render();

    // Start tracking clicks
    this.trackClicks();
  }

  handleTrail() {
    const currentUrl = window.location.pathname;
    const isHomePage = currentUrl === "/" || currentUrl === "";

    // RESET: Clear trail if on homepage
    if (isHomePage) {
      this.clearTrail();
      return;
    }

    // Check if we have a pending click (user clicked a link to get here)
    const pendingClick = this.getPendingClick();

    if (pendingClick && pendingClick.url === currentUrl) {
      // Get current trail
      let trail = this.getTrail();

      // FIRST: Check if we're going back to a page already in the trail
      const existingIndex = trail.findIndex(
        (item) => item.url === pendingClick.url
      );
      if (existingIndex !== -1) {
        // Trim trail to this point (user clicked a breadcrumb or went back)
        trail = trail.slice(0, existingIndex + 1);
        this.saveTrail(trail);
        this.clearPendingClick();
        return;
      }

      // SECOND: Check if we're navigating to a collection or page
      const isCollection = pendingClick.url.includes("/collections/");
      const isPage = pendingClick.url.includes("/pages/");
      const isProduct = pendingClick.url.includes("/products/");

      if (isCollection || isPage) {
        // ALWAYS reset trail for collections and pages - they start a new navigation path
        trail = [];
      } else if (isProduct) {
        // Product page - remove previous product if exists (allows browsing multiple products)
        if (trail.length > 0) {
          const lastItem = trail[trail.length - 1];
          const lastWasProduct = lastItem.url.includes("/products/");

          if (lastWasProduct) {
            trail.pop(); // Remove the product
          }
        }
      }

      // If trail is empty, initialize with Home
      if (trail.length === 0) {
        trail.push(this.getHomeItem());
      }

      // For products, extract the actual product title from the page
      // For collections/pages, use the clicked link title to avoid flickering
      let pageTitle = pendingClick.title;

      if (isProduct) {
        const productTitleEl = document.querySelector(
          'h1.product__title, .product__title, h1[itemprop="name"]'
        );
        if (productTitleEl) {
          pageTitle = this.cleanTitle(productTitleEl.textContent.trim());
        }
      } else {
        pageTitle = this.cleanTitle(pageTitle);
      }

      // Add the clicked page to trail
      trail.push({
        title: pageTitle,
        url: pendingClick.url,
      });

      // Limit trail length
      if (trail.length > this.MAX_TRAIL_LENGTH) {
        trail = [trail[0], ...trail.slice(-(this.MAX_TRAIL_LENGTH - 1))];
      }

      this.saveTrail(trail);
      this.clearPendingClick();
    } else {
      // No pending click - could be browser back button, direct visit, or external link
      // Check if current URL is in the existing trail (browser back button)
      let trail = this.getTrail();
      const existingIndex = trail.findIndex((item) => item.url === currentUrl);

      if (existingIndex !== -1) {
        // Current page is in trail - user likely used browser back button
        // Trim trail to this point
        trail = trail.slice(0, existingIndex + 1);
        this.saveTrail(trail);
      } else {
        // Current page not in trail - direct visit or external link
        // Clear trail and use fallback
        this.clearTrail();
      }
    }
  }

  getCurrentPageTitle(fallbackTitle) {
    // Try to get the actual page title from the current page
    // This is especially important for product pages where link text might be generic

    // Check if we're on a product page
    if (window.location.pathname.includes("/products/")) {
      // Try to get product title from the page
      const productTitle = document.querySelector(
        'h1.product__title, .product__title, h1[itemprop="name"]'
      );
      if (productTitle) {
        return this.cleanTitle(productTitle.textContent.trim());
      }
    }

    // Check if we're on a collection page
    if (window.location.pathname.includes("/collections/")) {
      // Try to get collection title from the page
      const collectionTitle = document.querySelector(
        "h1.collection-hero__title, .collection__title, h1"
      );
      if (collectionTitle) {
        return this.cleanTitle(collectionTitle.textContent.trim());
      }
    }

    // For other pages, try to get from breadcrumb container (fallback)
    const breadcrumbContainer = document.querySelector("#breadcrumb-container");
    if (breadcrumbContainer) {
      const lastItem = breadcrumbContainer.querySelector(
        '.breadcrumb__item:last-child .breadcrumb__text, .breadcrumb__item:last-child span[itemprop="name"]'
      );
      if (lastItem) {
        return this.cleanTitle(lastItem.textContent.trim());
      }
    }

    // Last resort: use the fallback title from the clicked link
    return this.cleanTitle(fallbackTitle);
  }

  cleanTitle(title) {
    // Remove common prefixes from titles
    const prefixes = [
      "Collection:",
      "Collections:",
      "Samling:",
      "Samlinger:",
      "Product:",
      "Products:",
      "Produkt:",
      "Produkter:",
    ];

    let cleanedTitle = title;

    for (const prefix of prefixes) {
      if (cleanedTitle.startsWith(prefix)) {
        cleanedTitle = cleanedTitle.substring(prefix.length).trim();
        break;
      }
    }

    return cleanedTitle;
  }

  trackClicks() {
    document.addEventListener("click", (e) => {
      const link = e.target.closest("a");
      if (!link) return;

      const href = link.getAttribute("href");
      if (!href) return;

      // Ignore hash links, javascript:, external links
      if (
        href.startsWith("#") ||
        href.startsWith("javascript:") ||
        href.startsWith("mailto:") ||
        href.startsWith("tel:")
      ) {
        return;
      }

      // Check if internal link
      const isInternal =
        href.startsWith("/") || href.includes(window.location.hostname);
      if (!isInternal) return;

      // Extract pathname
      let pathname = href.startsWith("/") ? href : new URL(href).pathname;

      // Get link title
      let title = this.extractLinkTitle(link);

      // Store as pending click
      const clickData = {
        title: title,
        url: pathname,
      };

      sessionStorage.setItem(this.PENDING_CLICK_KEY, JSON.stringify(clickData));
    });
  }

  extractLinkTitle(link) {
    // Try different methods to get a good title
    if (link.hasAttribute("title") && link.getAttribute("title").trim()) {
      return link.getAttribute("title").trim();
    }

    if (
      link.hasAttribute("aria-label") &&
      link.getAttribute("aria-label").trim()
    ) {
      return link.getAttribute("aria-label").trim();
    }

    // Get text content
    let text = link.textContent.trim();

    // If text is very long, try to find specific elements
    if (text.length > 100) {
      const selectors = [
        ".card__heading",
        ".card-information__text",
        "h2",
        "h3",
        "h4",
        ".product-card__title",
        ".collection-card__title",
      ];

      for (const selector of selectors) {
        const element = link.querySelector(selector);
        if (element) {
          text = element.textContent.trim();
          break;
        }
      }
    }

    // Clean up extra whitespace
    text = text.replace(/\s+/g, " ");

    return text || "Page";
  }

  render() {
    const container = document.querySelector("#breadcrumb-container");
    if (!container) {
      return;
    }

    const trail = this.getTrail();

    // Always render if we have a trail
    if (trail.length > 0) {
      this.renderTrail(container, trail);
    }
  }

  renderTrail(container, trail) {
    // Clear container
    container.innerHTML = "";

    // Build breadcrumbs with same structure as Liquid template
    trail.forEach((item, index) => {
      const isLast = index === trail.length - 1;

      // Add delimiter before each item except the first
      if (index > 0) {
        const delimiter = document.createElement("div");
        delimiter.className = "breadcrumb__delimiter";
        container.appendChild(delimiter);
      }

      // Create wrapper span with schema.org markup
      const wrapper = document.createElement("span");
      wrapper.setAttribute("itemprop", "itemListElement");
      wrapper.setAttribute("itemscope", "");
      wrapper.setAttribute("itemtype", "https://schema.org/ListItem");

      if (isLast) {
        // Current page - no link, just text
        const textSpan = document.createElement("span");
        textSpan.setAttribute("itemprop", "name");
        textSpan.textContent = item.title;
        wrapper.appendChild(textSpan);
      } else {
        // Clickable link
        const link = document.createElement("a");
        link.href = item.url;
        link.className = "focus-inset";
        link.setAttribute("itemprop", "item");

        const nameSpan = document.createElement("span");
        nameSpan.setAttribute("itemprop", "name");
        nameSpan.textContent = item.title;

        link.appendChild(nameSpan);
        wrapper.appendChild(link);
      }

      // Add position meta
      const position = document.createElement("meta");
      position.setAttribute("itemprop", "position");
      position.setAttribute("content", String(index + 1));
      wrapper.appendChild(position);

      container.appendChild(wrapper);
    });

    // Recalculate height for layout
    this.updateHeight();
  }

  updateHeight() {
    const breadcrumbEl = document.querySelector(".breadcrumb");
    if (breadcrumbEl) {
      const height = breadcrumbEl.getBoundingClientRect().height;
      document.documentElement.style.setProperty(
        "--breadcrumbs-height",
        `${height + 24}px`
      );
    }
  }

  // Storage methods
  getTrail() {
    try {
      const stored = sessionStorage.getItem(this.TRAIL_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      console.error("[Breadcrumb] Failed to get trail:", e);
      return [];
    }
  }

  saveTrail(trail) {
    try {
      sessionStorage.setItem(this.TRAIL_KEY, JSON.stringify(trail));
    } catch (e) {
      console.error("[Breadcrumb] Failed to save trail:", e);
    }
  }

  clearTrail() {
    try {
      sessionStorage.removeItem(this.TRAIL_KEY);
    } catch (e) {
      console.error("[Breadcrumb] Failed to clear trail:", e);
    }
  }

  getPendingClick() {
    try {
      const stored = sessionStorage.getItem(this.PENDING_CLICK_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      console.error("[Breadcrumb] Failed to get pending click:", e);
      return null;
    }
  }

  clearPendingClick() {
    try {
      sessionStorage.removeItem(this.PENDING_CLICK_KEY);
    } catch (e) {
      console.error("[Breadcrumb] Failed to clear pending click:", e);
    }
  }

  getHomeItem() {
    // Try to get home title from different sources
    let homeTitle = "Home"; // Default fallback

    // Try to find any home link on the page with title or text
    const homeLinks = document.querySelectorAll(
      'a[href="/"], a[href="' + window.location.origin + '"]'
    );

    for (const link of homeLinks) {
      // Prefer title attribute (usually has translation)
      if (link.hasAttribute("title") && link.getAttribute("title").trim()) {
        homeTitle = link.getAttribute("title").trim();
        break;
      }
      // Otherwise use link text if it's short (not a full sentence/paragraph)
      const linkText = link.textContent.trim();
      if (linkText && linkText.length < 20) {
        homeTitle = linkText;
        break;
      }
    }

    return {
      title: homeTitle,
      url: "/",
    };
  }

  // Public method to clear trail (for debugging)
  reset() {
    this.clearTrail();
    this.clearPendingClick();
    window.location.reload();
  }
}

// Initialize
if (typeof window !== "undefined") {
  window.breadcrumbTracker = new BreadcrumbTracker();

  // Expose reset method for debugging
  window.resetBreadcrumbs = () => {
    if (window.breadcrumbTracker) {
      window.breadcrumbTracker.reset();
    }
  };
}
